import { useEffect, useState } from "react";

// Global, per-user display mode for structured-data (JSON) fields. A single
// preference is shared across every structured-data editor and remembered across
// page reloads (localStorage), mirroring the word-wrap preference in CodeEditor.
// Structured-data fields (SchemaForm `form-json`, setting/connector value, ...)
// hold their value as a parsed object and derive the editor text, so switching
// the mode simply re-serializes: YAML mode shows/accepts pretty YAML, JSON mode
// shows/accepts pretty JSON. Default is "yaml" (the historical rendering).
export type StructuredDataDisplayMode = "yaml" | "json";

const STORAGE_KEY = "cloudomation.structuredDataDisplayMode";

const readStored = (): StructuredDataDisplayMode => {
    try {
        return localStorage.getItem(STORAGE_KEY) === "json" ? "json" : "yaml";
    } catch {
        return "yaml";
    }
};

let currentMode: StructuredDataDisplayMode = readStored();
const listeners = new Set<() => void>();

export const getStructuredDataDisplayMode = (): StructuredDataDisplayMode => currentMode;

export const setStructuredDataDisplayMode = (mode: StructuredDataDisplayMode): void => {
    if (mode === currentMode) return;
    currentMode = mode;
    try {
        localStorage.setItem(STORAGE_KEY, mode);
    } catch {
        /* ignore storage errors (e.g. private mode) — preference stays session-only */
    }
    listeners.forEach((listener) => listener());
};

export const toggleStructuredDataDisplayMode = (): void =>
    setStructuredDataDisplayMode(currentMode === "yaml" ? "json" : "yaml");

// React 17-compatible external-store subscription (no useSyncExternalStore).
// Every subscriber re-renders when the shared mode changes, so all open
// structured-data editors switch representation together.
export const useStructuredDataDisplayMode = (): StructuredDataDisplayMode => {
    const [mode, setMode] = useState<StructuredDataDisplayMode>(currentMode);
    useEffect(() => {
        const listener = () => setMode(currentMode);
        listeners.add(listener);
        // Re-sync in case the mode changed between render and effect commit.
        listener();
        return () => {
            listeners.delete(listener);
        };
    }, []);
    return mode;
};
